import { setLabel } from './i18n';
import type { CameraPose } from '../data/types';
import type { Pose } from '../controls/types';
import type { Vec2 } from '../geo/types';
import type { PaintingMinimap, PaintingMinimapMark } from './types';
import { createElement, getUiRoot, setText } from './dom';
import { createIcon } from './icons';
import { createPaintingMinimapModel, fitPaintingMinimap } from './paintingMinimapModel';
import paintingUrl from '../../docs/reference/cornelis-anthonisz-1538.jpg?url';
import detailUrl from '../assets/painting-1538-minimap.webp?url';
import { createTimeClock } from './timeClock';
import { CityTime } from '../time/cityTime';
import { layoutToWorld } from '../core/coords';

const ZOOMS = [1, 1.5, 2, 3, 4, 6, 8, 10, 12] as const;
/** Half the mark's width: the mark is centred on its place. */
const MARK_HALF_PX = 7;

function button(parent: HTMLElement, text: string, label: string, action: () => void) {
  const el = createElement('button', 'painting-minimap-button', parent);
  el.type = 'button';
  setText(el, text);
  setLabel(el, 'title', label);
  setLabel(el, 'aria-label', label);
  el.addEventListener('click', (event) => {
    // Pointer users return straight to movement. Keyboard activation keeps focus
    // so Tab, Enter and Space can still operate the minimap accessibly.
    if (event.detail > 0) el.blur();
    action();
  });
  // Space activates a focused button; it must not also make the player ascend.
  el.addEventListener('keydown', (event) => {
    if (
      ['Space', 'Enter', 'ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(event.code)
    ) {
      event.stopPropagation();
    }
  });
  return el;
}

function createMarker(viewport: HTMLElement) {
  const marker = createElement('div', 'painting-minimap-marker', viewport);
  marker.setAttribute('role', 'img');
  setLabel(marker, 'aria-label', 'Your position and direction in the painting');
  const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  svg.setAttribute('viewBox', '0 0 32 32');
  svg.setAttribute('aria-hidden', 'true');
  const arrow = document.createElementNS(svg.namespaceURI, 'path');
  arrow.setAttribute('d', 'M16 3 L22 12 L16 10 L10 12 Z');
  const dot = document.createElementNS(svg.namespaceURI, 'circle');
  dot.setAttribute('cx', '16');
  dot.setAttribute('cy', '16');
  dot.setAttribute('r', '4');
  svg.append(arrow, dot);
  marker.append(svg);
  return { marker, arrow };
}

function createView(onHelp: () => void, onZoom: (delta: number) => void, onRecenter: () => void) {
  const panel = createElement('section', 'painting-minimap');
  panel.id = 'painting-minimap';
  setLabel(panel, 'aria-label', 'Painting minimap');
  const header = createElement('div', 'painting-minimap-header', panel);
  const help = button(header, '?', 'Help', onHelp);
  help.classList.add('painting-minimap-help');
  help.setAttribute('aria-keyshortcuts', 'H');
  const recenter = button(header, '', 'Recenter painting on your position', onRecenter);
  const recenterIcon = createIcon('recenter', 18);
  recenterIcon.setAttribute('aria-hidden', 'true');
  recenter.append(recenterIcon);
  recenter.classList.add('painting-minimap-center');
  const minus = button(header, '−', 'Zoom out of painting', () => {
    onZoom(-1);
  });
  const plus = button(header, '+', 'Zoom into painting', () => {
    onZoom(1);
  });
  minus.classList.add('painting-minimap-minus');
  plus.classList.add('painting-minimap-plus');
  const viewport = createElement('div', 'painting-minimap-viewport', panel);
  viewport.tabIndex = 0;
  viewport.setAttribute('role', 'group');
  setLabel(viewport, 'aria-label', 'Explore painting: drag or use arrow keys to pan');
  setLabel(viewport, 'title', 'Drag to explore the painting; recenter to follow your position');
  const image = createElement('img', 'painting-minimap-image', viewport);
  image.alt = 'Amsterdam in 1538, painted by Cornelis Anthonisz';
  image.draggable = false;
  const finds = createElement('div', 'painting-minimap-finds', viewport);
  const pin = createMarker(viewport);
  pin.marker.hidden = true;
  const message = createElement('span', 'painting-minimap-message', viewport);
  setText(message, 'Loading painting…');
  return { panel, viewport, image, finds, message, minus, plus, recenter, help, ...pin };
}

class Minimap implements PaintingMinimap {
  private readonly model;
  private readonly view;
  private readonly clock;
  private observer: ResizeObserver | null = null;
  private viewport: Vec2 = [0, 0];
  private pose: Pose | null = null;
  private zoomIndex = 0;
  private visible = true;
  private mounted = false;
  private ready = false;
  private detail: HTMLImageElement | null = null;
  private focus: Vec2 | null = null;
  private drag: { id: number; x: number; y: number } | null = null;
  private marks: readonly PaintingMinimapMark[] = [];
  private markEls: HTMLElement[] = [];

  constructor(
    painterView: CameraPose,
    onHelp: () => void,
    private readonly time: CityTime
  ) {
    this.model = createPaintingMinimapModel(painterView);
    this.view = createView(
      onHelp,
      (delta) => {
        this.zoom(delta);
      },
      () => {
        this.focus = null;
        this.render();
      }
    );
    this.clock = createTimeClock(this.view.panel, time);
    this.bindPanning();
    this.view.image.onload = () => {
      this.ready = true;
      this.view.message.hidden = true;
      this.render();
    };
    this.view.image.onerror = () => {
      this.ready = false;
      this.view.message.hidden = false;
      setText(this.view.message, 'Painting unavailable');
      this.render();
    };
  }

  mount(): void {
    if (this.mounted) return;
    this.mounted = true;
    getUiRoot().append(this.view.panel);
    this.view.image.src = paintingUrl;
    this.observer = new ResizeObserver(() => {
      this.resize();
    });
    this.observer.observe(this.view.viewport);
    this.resize();
  }

  setVisible(visible: boolean): void {
    const active = document.activeElement;
    const focusInside = this.view.panel.contains(active);
    this.visible = visible;
    this.view.panel.hidden = !visible;
    if (!visible && focusInside && active instanceof HTMLElement) active.blur();
    if (visible) this.resize();
  }

  setMarks(marks: readonly PaintingMinimapMark[]): void {
    this.marks = marks;
    this.view.finds.replaceChildren();
    this.markEls = marks.map((mark) => {
      const el = createElement('div', 'painting-minimap-find', this.view.finds);
      el.setAttribute('role', 'img');
      setLabel(el, 'aria-label', mark.name);
      setLabel(el, 'title', mark.name);
      el.hidden = true;
      return el;
    });
    this.render();
  }

  update(pose: Pose): void {
    this.clock.update(this.time.snapshot());
    const previous = this.pose;
    this.pose = pose;
    if (
      previous &&
      previous.position[0] === pose.position[0] &&
      previous.position[2] === pose.position[2] &&
      previous.yaw === pose.yaw
    )
      return;
    this.render();
  }

  resize(): void {
    if (!this.mounted || !this.visible) return;
    this.viewport = [this.view.viewport.clientWidth, this.view.viewport.clientHeight];
    this.render();
  }

  private zoom(delta: number): void {
    this.zoomIndex = Math.max(0, Math.min(ZOOMS.length - 1, this.zoomIndex + delta));
    if (this.zoomIndex > 0) this.loadDetail();
    this.render();
  }

  private currentFit() {
    const marker = this.pose ? this.model.marker(this.pose.position, this.pose.yaw) : null;
    return fitPaintingMinimap(
      this.viewport,
      this.model.imageSize,
      ZOOMS[this.zoomIndex] ?? 1,
      this.focus ?? marker?.pixel,
      'cover'
    );
  }

  private pan(dx: number, dy: number): void {
    if (!this.ready || (!dx && !dy)) return;
    const fit = this.currentFit();
    const x = Math.min(0, Math.max(this.viewport[0] - fit.width, fit.x + dx));
    const y = Math.min(0, Math.max(this.viewport[1] - fit.height, fit.y + dy));
    this.focus = [
      fit.width <= this.viewport[0]
        ? this.model.imageSize[0] / 2
        : (this.viewport[0] / 2 - x) / fit.scale,
      fit.height <= this.viewport[1]
        ? this.model.imageSize[1] / 2
        : (this.viewport[1] / 2 - y) / fit.scale,
    ];
    this.render();
  }

  private bindPanning(): void {
    const viewport = this.view.viewport;
    viewport.addEventListener('pointerdown', (event) => {
      if (event.button !== 0 || !this.ready || this.drag) return;
      // Keep pointer interaction from leaving keyboard movement focused on the map.
      if (document.activeElement instanceof HTMLElement) document.activeElement.blur();
      event.preventDefault();
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
      viewport.setPointerCapture(event.pointerId);
      viewport.classList.add('is-dragging');
    });
    viewport.addEventListener('pointermove', (event) => {
      if (this.drag?.id !== event.pointerId) return;
      this.pan(event.clientX - this.drag.x, event.clientY - this.drag.y);
      this.drag = { id: event.pointerId, x: event.clientX, y: event.clientY };
    });
    const release = () => {
      this.drag = null;
      viewport.classList.remove('is-dragging');
    };
    viewport.addEventListener('lostpointercapture', release);
    viewport.addEventListener('pointercancel', release);
    viewport.addEventListener('pointerup', (event) => {
      if (viewport.hasPointerCapture(event.pointerId))
        viewport.releasePointerCapture(event.pointerId);
      release();
    });
    viewport.addEventListener('keydown', (event) => {
      const delta: Record<string, Vec2> = {
        ArrowLeft: [32, 0],
        ArrowRight: [-32, 0],
        ArrowUp: [0, 32],
        ArrowDown: [0, -32],
      };
      const offset = delta[event.code];
      if (offset) {
        event.preventDefault();
        event.stopPropagation();
        this.pan(...offset);
      } else if (event.code === 'Escape') {
        viewport.blur();
      }
    });
  }

  // The small painting is available immediately. Fetch museum detail only when zoom is used;
  // the 4000px WebP is derived from the original in docs/reference, without changing its crop.
  private loadDetail(): void {
    if (this.detail) return;
    this.detail = new Image();
    this.detail.onload = () => {
      this.view.image.src = detailUrl;
    };
    this.detail.src = detailUrl;
  }

  private render(): void {
    if (!this.visible || !this.mounted) return;
    const zoom = ZOOMS[this.zoomIndex] ?? 1;
    const marker = this.pose ? this.model.marker(this.pose.position, this.pose.yaw) : null;
    const fit = this.currentFit();
    const { image, minus, plus } = this.view;
    image.style.width = `${fit.width}px`;
    image.style.height = `${fit.height}px`;
    image.style.transform = `translate(${fit.x}px, ${fit.y}px)`;
    this.view.panel.dataset.zoom = String(zoom);
    this.view.panel.dataset.following = String(this.focus === null);
    this.view.recenter.disabled = !this.ready || this.focus === null;
    minus.disabled = !this.ready || this.zoomIndex === 0;
    plus.disabled = !this.ready || this.zoomIndex === ZOOMS.length - 1;
    this.renderMarks(fit);
    this.view.marker.hidden = !this.ready || !marker?.inside;
    if (!marker?.inside) return;
    this.view.marker.style.transform = `translate(${fit.x + marker.pixel[0] * fit.scale - 16}px, ${fit.y + marker.pixel[1] * fit.scale - 16}px)`;
    this.view.arrow.setAttribute('visibility', marker.direction ? 'visible' : 'hidden');
    if (marker.direction) {
      const angle = (Math.atan2(marker.direction[0], -marker.direction[1]) * 180) / Math.PI;
      this.view.arrow.setAttribute('transform', `rotate(${angle} 16 16)`);
    }
  }

  // A mark is drawn only once the painting is loaded and only where its place lies in the image.
  private renderMarks(fit: ReturnType<typeof fitPaintingMinimap>): void {
    this.marks.forEach((mark, index) => {
      const el = this.markEls[index];
      const pixel = this.ready ? this.model.projectGround(layoutToWorld(mark.point, 0)) : null;
      if (!el) return;
      const inside =
        pixel !== null &&
        pixel[0] >= 0 &&
        pixel[0] <= this.model.imageSize[0] &&
        pixel[1] >= 0 &&
        pixel[1] <= this.model.imageSize[1];
      el.hidden = !inside;
      if (!pixel || !inside) return;
      el.style.transform = `translate(${fit.x + pixel[0] * fit.scale - MARK_HALF_PX}px, ${fit.y + pixel[1] * fit.scale - MARK_HALF_PX}px)`;
    });
  }

  dispose(): void {
    this.observer?.disconnect();
    this.view.image.onload = this.view.image.onerror = null;
    if (this.detail) this.detail.onload = null;
    this.view.panel.remove();
  }
}

export function createPaintingMinimap(
  painterView: CameraPose,
  onHelp: () => void,
  time = new CityTime()
): PaintingMinimap {
  return new Minimap(painterView, onHelp, time);
}
